import { Alert, Button, Input } from "antd";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { LogoWhite } from "../../../assets/svg/Svgs";
import { APP_NAME, LOGIN } from "../../../constants/constants";
import {
  API_OTP_REQUEST,
  API_OTP_RESET_PASSWORD,
} from "../../../constants/apis";
import useSet from "../../../hooks/common/useSet";
import { currentYear } from "../../../utils/helpers";

/**
 * Phone-OTP password reset:
 *  Step 1: enter phone -> request OTP (purpose=password_reset)
 *  Step 2: enter code + new password -> reset
 */
export const OtpResetPage = () => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);
  const { uploadData, error, submitting } = useSet();
  const navigate = useNavigate();

  const requestOtp = (e: any) => {
    e.preventDefault();
    setClientError(null);
    if (!phone) return setClientError("Phone number is required.");
    uploadData({
      api: API_OTP_REQUEST,
      params: { phone_number: phone, purpose: "password_reset" },
      onSuccess: () => setStep(2),
      successMessage: "If that phone number is registered, a code has been sent.",
    });
  };

  const resetPassword = (e: any) => {
    e.preventDefault();
    setClientError(null);
    if (!code) return setClientError("Code is required.");
    if (newPassword.length < 8)
      return setClientError("Password must be at least 8 characters.");
    if (newPassword !== confirmPassword)
      return setClientError("Passwords do not match.");

    uploadData({
      api: API_OTP_RESET_PASSWORD,
      params: { phone_number: phone, code, new_password: newPassword },
      onSuccess: () => setStep(3),
      successMessage: "Password updated. You can now log in.",
    });
  };

  const errorMessage =
    clientError ||
    (() => {
      if (!error) return null;
      if (typeof error === "string") return error;
      if (error?.message) return error.message;
      if (error?.non_field_errors)
        return Array.isArray(error.non_field_errors)
          ? error.non_field_errors[0]
          : error.non_field_errors;
      if (error?.code) return Array.isArray(error.code) ? error.code[0] : error.code;
      if (error?.new_password)
        return Array.isArray(error.new_password)
          ? error.new_password[0]
          : error.new_password;
      if (typeof error === "object") {
        const k = Object.keys(error)[0];
        const v = error[k];
        return Array.isArray(v) ? v[0] : v;
      }
      return "An error occurred";
    })();

  return (
    <Wrapper className="vh100 bgGradient white flexColumn">
      <img id="overlay" alt={APP_NAME} src={LogoWhite} />
      <div className="flexGrow container flexCenter">
        <FormWrapper className="r15 whiteBg black flexColumn gap20">
          <div className="flexNullCenter gap15">
            <img
              alt={APP_NAME}
              src={LogoWhite}
              width={40}
              height={40}
              style={{ filter: "brightness(2000%)" }}
            />
            <div className="extraBold" style={{ fontSize: 25 }}>
              everpesa
            </div>
          </div>

          <div className="extraBold textDark" style={{ fontSize: 22 }}>
            {step === 1 && "Reset your password"}
            {step === 2 && "Enter code & set new password"}
            {step === 3 && "Password updated"}
          </div>

          <div className="light font13">
            {step === 1 &&
              "Enter the phone number on your account. We'll send you a 6-digit code via SMS."}
            {step === 2 &&
              `We sent a code to ${phone}. Enter it along with your new password below.`}
            {step === 3 && "You can now log in with your new password."}
          </div>

          {step === 1 && (
            <form onSubmit={requestOtp} className="flexColumn gap15">
              <Input
                size="large"
                placeholder="+2567XXXXXXXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoFocus
              />
              {errorMessage && (
                <Alert className="font12" message={errorMessage} type="error" showIcon />
              )}
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitting}
                block
                className="capitalize noShadow r7"
              >
                Send Code
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={resetPassword} className="flexColumn gap15">
              <Input
                size="large"
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={8}
                autoFocus
              />
              <Input.Password
                size="large"
                placeholder="New password (min 8 chars)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <Input.Password
                size="large"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {errorMessage && (
                <Alert className="font12" message={errorMessage} type="error" showIcon />
              )}
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitting}
                block
                className="capitalize noShadow r7"
              >
                Reset Password
              </Button>
              <Button type="link" onClick={() => setStep(1)} block>
                Use a different phone number
              </Button>
            </form>
          )}

          {step === 3 && (
            <Button
              type="primary"
              size="large"
              block
              onClick={() => navigate(`/auth/${LOGIN}`)}
            >
              Go to Login
            </Button>
          )}

          {step !== 3 && (
            <div className="flexCenter gap5">
              <div className="textCenter font12 light">Remember your password?</div>
              <Link to={`/auth/${LOGIN}`} className="font13 main semiBold">
                Login
              </Link>
            </div>
          )}
        </FormWrapper>
      </div>

      <div className="font12 textCenter" style={{ padding: 30 }}>
        © {currentYear()} {APP_NAME}. All rights reserved.
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  padding: 40px 0;
  #overlay {
    opacity: 0.1;
    position: absolute;
    left: -10%;
    bottom: -10%;
    width: 700px;
    pointer-events: none;
  }
`;

const FormWrapper = styled.div`
  max-width: 460px;
  width: 100%;
  padding: 30px;
  margin: 40px auto;
`;
